
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

export default function Flow() {
  const steps = [
    {
      step: '01',
      title: 'お問い合わせ',
      description: 'お電話またはWebフォームからお気軽にお問い合わせください',
      details: [
        'Webフォームは24時間受付',
        '営業時間内（8:00〜17:00）は即日回答',
        '土日でも緊急対応可能'
      ],
      icon: 'ri-phone-line',
      duration: '即日',
      image: 'Professional customer service representative answering phone call in modern office, friendly business communication, corporate customer support'
    },
    {
      step: '02',
      title: 'ヒアリング・現地調査',
      description: '配送内容やご要望を詳しくお聞きし、必要に応じて現地調査を実施',
      details: [
        '荷物の種類・サイズ・重量確認',
        '配送ルート・頻度の調査',
        '特別な要件のヒアリング'
      ],
      icon: 'ri-search-line',
      duration: '1-2日',
      image: 'Professional logistics consultant conducting site survey at warehouse, clipboard and measuring equipment, professional business assessment'
    },
    {
      step: '03',
      title: 'お見積もり・提案',
      description: '最適なプランとお見積もりを作成し、詳細なご提案をいたします',
      details: [
        '詳細な料金明細を提示',
        '複数プランからの選択肢提示',
        '改善効果の予測レポート'
      ],
      icon: 'ri-file-text-line',
      duration: '2-3日',
      image: 'Business proposal presentation with charts and logistics plans, professional meeting room, detailed cost analysis documents'
    },
    {
      step: '04',
      title: 'ご契約・スケジュール調整',
      description: 'ご提案内容にご納得いただけましたら、契約手続きを進めます',
      details: [
        '契約書の作成・締結',
        '配送スケジュールの調整',
        '専任チームのアサイン'
      ],
      icon: 'ri-file-check-line',
      duration: '1-2日',
      image: 'Professional contract signing ceremony in modern office, business handshake, official business documents on table'
    },
    {
      step: '05',
      title: '配送開始・完了報告',
      description: '実際の配送業務を開始し、完了後は詳細な報告をいたします',
      details: [
        'リアルタイムでの配送状況報告',
        '配送完了時の詳細レポート',
        '継続的なサービス改善提案'
      ],
      icon: 'ri-truck-line',
      duration: '継続',
      image: 'Successful delivery completion with professional driver and satisfied customer, modern logistics truck, professional service completion'
    }
  ];

  const supportServices = [
    {
      title: '24時間サポート',
      description: '緊急時や配送状況の確認はいつでもお気軽に',
      icon: 'ri-24-hours-line'
    },
    {
      title: 'リアルタイム追跡',
      description: 'GPS技術で配送状況をリアルタイムで確認',
      icon: 'ri-map-pin-line'
    },
    {
      title: '専任担当制',
      description: 'お客様専任の担当者が一貫してサポート',
      icon: 'ri-user-star-line'
    },
    {
      title: '定期レビュー',
      description: '月次でのサービス品質レビューと改善提案',
      icon: 'ri-line-chart-line'
    }
  ];

  const documents = [
    {
      name: '運送約款',
      description: '運送サービスの詳細な条件・規約',
      icon: 'ri-file-text-line'
    },
    {
      name: '保険証書',
      description: '貨物保険・運送業者賠償責任保険',
      icon: 'ri-shield-check-line'
    },
    {
      name: '許可証',
      description: '一般貨物運送事業許可証・軽貨物運送業届出書',
      icon: 'ri-award-line'
    },
    {
      name: '料金表',
      description: '詳細な料金体系・追加オプション料金',
      icon: 'ri-price-tag-3-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* ヒーローセクション */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(30, 58, 138, 0.8)), url('https://public.readdy.ai/ai/img_res/4871e1b994f690fc9fbb0044f4667a6d.jpg')`
        }}
      >
        <div className="text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">ご依頼の流れ</h1>
          <p className="text-xl text-blue-200">Service Flow</p>
        </div>
      </section>

      {/* 流れの概要 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              5つのステップで安心サポート
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              お問い合わせから配送完了まで、一貫したサポート体制でお客様をサポートいたします。
            </p>
          </div>

          {/* ステップの詳細 */}
          <div className="space-y-20">
            {steps.map((step, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-start mb-6">
                    <div className="bg-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mr-4 flex-shrink-0">
                      {step.step}
                    </div>
                    <div>
                      <div className="flex items-center mb-2">
                        <h3 className="text-2xl md:text-3xl font-bold text-blue-900 mr-4">
                          {step.title}
                        </h3>
                        <span className="bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-sm font-semibold">
                          {step.duration}
                        </span>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                        <i className={`${step.icon} text-blue-900 text-xl`}></i>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    <h4 className="font-semibold text-blue-900">詳細内容:</h4>
                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start text-gray-700">
                          <i className="ri-check-line text-green-500 mr-3 text-lg mt-0.5 flex-shrink-0"></i>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {index === 0 && (
                    <Link
                      to="/contact"
                      className="inline-flex items-center bg-blue-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      今すぐお問い合わせ
                      <i className="ri-arrow-right-line ml-2"></i>
                    </Link>
                  )}
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img
                    src={index === 0 ? "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/50615b8aa52d8bcf75777e7257b37a67.jpeg" : index === 1 ? "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/56cafbf74fd5daa0fb4e7dbc924a9a62.jpeg" : index === 2 ? "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/9299fad20ad8cf1eec5ddb0b1b9285f0.jpeg" : index === 3 ? "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/a3c53c42a288f52123c4e0f2df4c2f52.jpeg" : "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/949d34f18ce70f53295c56f8e89be261.jpeg"}
                    alt={step.title}
                    className="rounded-xl shadow-lg w-full h-auto object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* サポートサービス */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              充実のサポート体制
            </h2>
            <p className="text-xl text-gray-600">
              配送開始後も安心の継続サポート
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {supportServices.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${service.icon} text-2xl text-yellow-400`}></i>
                </div>
                <h3 className="text-lg font-bold text-blue-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 必要書類 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              関連書類・資料
            </h2>
            <p className="text-xl text-gray-600">
              ご契約時に提示・提供する書類一覧
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {documents.map((doc, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 text-center">
                <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${doc.icon} text-yellow-400 text-xl`}></i>
                </div>
                <h3 className="font-bold text-blue-900 mb-2">{doc.name}</h3>
                <p className="text-gray-700 text-sm">{doc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* よくある質問 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              よくあるご質問
            </h2>
            <p className="text-xl text-gray-600">
              お問い合わせ前によくいただくご質問
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: 'お見積もりは無料ですか？',
                answer: 'はい、お見積もりは完全無料です。現地調査が必要な場合も費用はかかりません。'
              },
              {
                question: '急な配送依頼にも対応できますか？',
                answer: '24時間対応体制を整えており、緊急配送にも柔軟に対応いたします。まずはお電話でご相談ください。'
              },
              {
                question: '配送できる荷物に制限はありますか？',
                answer: '危険物や法令で禁止されている物品以外は基本的に配送可能です。詳細はお問い合わせ時にご相談ください。'
              },
              {
                question: '保険は適用されますか？',
                answer: '貨物保険および運送業者賠償責任保険に加入しており、万が一の場合も安心です。'
              },
              {
                question: '定期配送の契約期間はどのくらいですか？',
                answer: '最短1ヶ月から承っております。お客様のご都合に合わせて柔軟に対応いたします。'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="font-bold text-blue-900 mb-3 flex items-start">
                  <i className="ri-question-line mr-2 text-yellow-400 mt-1 flex-shrink-0"></i>
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed ml-6">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            まずはお気軽にお問い合わせください
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            専門スタッフが丁寧にヒアリングし、最適なプランをご提案いたします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              お問い合わせ
            </Link>
            <a
              href="tel:075-123-4567"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>
              075-123-4567
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
